import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  Layers,
  BarChart2,
  Brain,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  UserRound,
  Wrench,
  LogOut,
  Tag,
  Server,
  Sparkles,
  AudioWaveform,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TraceabilityQuickAccess } from './TraceabilityQuickAccess';
import { TRACEABILITY, ANALYTICS, ML, TOOL_RECOMMENDER } from '@/constants/modules';

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
};

type NavItemConfig = {
  id: string;
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  testId: string;
};

type SidebarNavItemProps = {
  item: NavItemConfig;
  collapsed: boolean;
  currentPath: string;
  t: (key: string) => string;
  onLinkClick?: () => void;
};

function SidebarNavItem({ item, collapsed, currentPath, t, onLinkClick }: SidebarNavItemProps) {
  const isActive = currentPath === item.to || currentPath.startsWith(item.to + '/');

  return (
    <Link
      to={item.to}
      data-testid={item.testId}
      onClick={onLinkClick}
      title={t(item.labelKey)}
      className={clsx(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'border-l-2 border-primary bg-accent text-accent-foreground font-semibold'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      <span className="shrink-0">{item.icon}</span>
      {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
    </Link>
  );
}

export function Sidebar({ isOpen, onClose, collapsed, onCollapsedChange }: SidebarProps) {
  const { t } = useTranslation();
  const { tokenService, logout } = useAuth();
  const location = useLocation();
  const isLg = useMediaQuery('(min-width: 1024px)');

  const modules = tokenService?.getModules() ?? [];
  const role = tokenService?.getRole() ?? 'user';
  const isAdmin = role === 'admin' || role === 'super-admin';

  const navItems: NavItemConfig[] = [
    {
      id: 'labelize',
      to: '/labelize',
      labelKey: 'sidebar.labelize',
      icon: <Tag className="h-5 w-5" />,
      testId: 'nav-labelize',
    },
    ...(modules.includes(TRACEABILITY)
      ? [
          {
            id: 'traceability',
            to: '/traceability/tools',
            labelKey: 'sidebar.traceability',
            icon: <Layers className="h-5 w-5" />,
            testId: 'nav-traceability',
          },
          {
            id: 'workshop',
            to: '/traceability/workshop',
            labelKey: 'sidebar.workshop',
            icon: <Wrench className="h-5 w-5" />,
            testId: 'nav-workshop',
          },
        ]
      : []),
    ...(modules.includes(ANALYTICS)
      ? [
          {
            id: 'analytics',
            to: '/analytics',
            labelKey: 'sidebar.analytics',
            icon: <BarChart2 className="h-5 w-5" />,
            testId: 'nav-analytics',
          },
        ]
      : []),
    ...(modules.includes(ML)
      ? [
          {
            id: 'ml',
            to: '/ml',
            labelKey: 'sidebar.ml',
            icon: <Brain className="h-5 w-5" />,
            testId: 'nav-ml',
          },
          {
            id: 'signals',
            to: '/signals',
            labelKey: 'signals.nav',
            icon: <AudioWaveform className="h-5 w-5" />,
            testId: 'nav-signals',
          },
        ]
      : []),
    ...(modules.includes(TOOL_RECOMMENDER)
      ? [
          {
            id: 'tool-recommender',
            to: '/tool-recommender',
            labelKey: 'sidebar.toolRecommender',
            icon: <Sparkles className="h-5 w-5" />,
            testId: 'nav-tool-recommender',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            id: 'admin',
            to: '/admin',
            labelKey: 'sidebar.admin',
            icon: <ShieldCheck className="h-5 w-5" />,
            testId: 'nav-admin',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            id: 'backend-config',
            to: '/admin/backend-config',
            labelKey: 'sidebar.backendConfig',
            icon: <Server className="h-5 w-5" />,
            testId: 'nav-backend-config',
          },
        ]
      : []),
  ];

  function handleCollapseToggle() {
    onCollapsedChange(!collapsed);
  }

  const desktopWidth = collapsed ? 'w-[52px]' : 'w-[200px]';

  // Separate render function so mobile always passes isCollapsed=false
  // while desktop respects the collapsed prop.
  const renderContent = (isCollapsed: boolean) => (
    <nav className="flex h-full flex-col py-4">
      <TraceabilityQuickAccess collapsed={isCollapsed} onLinkClick={onClose} />
      <ul className="flex flex-col gap-1 px-2 flex-1 overflow-y-auto min-h-0">
        {navItems.map((item) => (
          <li key={item.id}>
            <SidebarNavItem
              item={item}
              collapsed={isCollapsed}
              currentPath={location.pathname}
              t={t}
              onLinkClick={onClose}
            />
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-1 px-2 shrink-0">
        <SidebarNavItem
          item={{
            id: 'profile',
            to: '/profile',
            labelKey: 'sidebar.profile',
            icon: <UserRound className="h-5 w-5" />,
            testId: 'nav-profile',
          }}
          collapsed={isCollapsed}
          currentPath={location.pathname}
          t={t}
          onLinkClick={onClose}
        />
        <button
          type="button"
          onClick={logout}
          title={t('sidebar.disconnect')}
          aria-label={t('sidebar.disconnect')}
          className={clsx(
            'flex items-center rounded-lg py-2 text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
            isCollapsed ? 'w-10 justify-center px-0' : 'w-full justify-start px-3'
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" aria-hidden />
          {!isCollapsed && (
            <span className="ml-3 text-sm font-medium">{t('sidebar.disconnect')}</span>
          )}
        </button>
        {isLg && (
          <button
            type="button"
            data-testid="sidebar-collapse-toggle"
            onClick={handleCollapseToggle}
            title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            className={clsx(
              'flex items-center justify-center rounded-lg py-2 text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
              isCollapsed ? 'w-10 px-0' : 'w-full px-3'
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" aria-hidden />
            ) : (
              <ChevronLeft className="h-5 w-5" aria-hidden />
            )}
            {!isCollapsed && (
              <span className="ml-2 text-sm font-medium">{t('sidebar.collapse')}</span>
            )}
          </button>
        )}
      </div>
    </nav>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          data-testid="sidebar-backdrop"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer — always expanded, never collapsed */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-64 border-r border-border bg-background dark:bg-sidebar transition-transform duration-300 lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-label={t('sidebar.mobileNavigation')}
      >
        {renderContent(false)}
      </aside>

      {/* Desktop sidebar — respects collapsed state */}
      <aside
        className={clsx(
          'hidden lg:flex flex-col border-r border-border bg-background dark:bg-sidebar transition-all duration-300 overflow-hidden',
          desktopWidth,
          'shrink-0'
        )}
        aria-label={t('sidebar.desktopNavigation')}
      >
        {renderContent(collapsed)}
      </aside>
    </>
  );
}
